import { Icon } from "../components/Icon";
import { useOpenerFocus } from "../components/useOpenerFocus";
import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { Workspace } from "../components/Shell";
import { LibraryTabs, useLibraryCounts } from "../components/LibraryTabs";
import { PieceIcon } from "../components/EquipmentIcon";
import {
  CUSTOM_EQUIPMENT_OPEN, GLYPH_PREFIX, ICON_BUCKET, allEquipmentKeys, builtInsToRestore, copyName,
  equipmentName, equipmentPageSections, isCustomKey, settingWrite, usageCounts,
  type EquipmentFilter, type EquipmentItem, type EquipmentSetting,
} from "../lib/equipmentLibrary";
import {
  EquipmentConfirmWindow, EquipmentEditor, type EditTarget, type EquipmentConfirm,
} from "../components/EquipmentWindows";

/**
 * EQUIPMENT — the Library's fourth list (his direction, 2026-09-23, point 6: *"we need a place to
 * create equipment no? So maybe in the library? As a category. So we can hide/unhide equipment,
 * delete, copy and so on. Edit..create."*), and his decision the same morning: *"the custom equipment
 * library/editor only available at the web app … Plus we need a back to defaults option somewhere so
 * he can go back safely."*
 *
 * WHAT IT DOES: every piece the app knows, grouped as the phone groups it, plus the coach's OWN
 * pieces first. Per piece (the ⋯ menu): Edit (name and icon), Copy (a new piece of his own), Hide /
 * Show (whether his builder offers it), Restore default (a built-in he changed), Delete (his own
 * piece). "Changed" lists only the built-ins he altered — the place to go back — and "Restore all
 * defaults" takes every one of them back at once, never touching his own pieces.
 *
 * ⚠️ TWO PARTS WAIT, SAID RATHER THAN FAKED: CREATING a piece of his own (New, Copy) waits for the
 * phone build that can name it (`CUSTOM_EQUIPMENT_OPEN`), and UPLOADING an icon waits for the icons
 * bucket (`ICON_UPLOAD_OPEN`). Neither control is drawn until then.
 *
 * ⚠️ NOTHING HERE REWRITES AN EXERCISE. An exercise stores the key; a rename, an icon or a hide
 * changes what is DRAWN. Deleting one of his own pieces leaves the exercises that use it naming it —
 * the phone and this page then call it by the name it was created with.
 */
export function Equipment({ foreground = true }: { foreground?: boolean }) {
  const [settings, setSettings] = useState<EquipmentSetting[] | null>(null);
  const [usage, setUsage] = useState<Map<string, number>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<EquipmentFilter>("all");
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [confirming, setConfirming] = useState<EquipmentConfirm | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const libraryCounts = useLibraryCounts();
  /* JOB 3 STEP 5 PAGE 6 — the editor is mounted ON DEMAND (it sat in the page as a closed dialog with its name field and a
     hidden file input); focus goes back to `New equipment`, or to the row's ⋯ when a menu item opened it. */
  const remember = useOpenerFocus(editing !== null, heading);
  const rowMenuButton = (from: HTMLElement) =>
    from.closest(".equipment-menu-anchor")?.querySelector<HTMLElement>(".equipment-menu-button") ?? null;

  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // ⚠️ HIS OWN ROWS, SAID OUT LOUD: once a client may read their coach's equipment (W62 step
      // C(b)), RLS alone would also hand a coach who is somebody's CLIENT that other coach's rows —
      // and this page would offer to restore or delete them. So the read names the owner.
      const { data: sessionData } = await supabase.auth.getSession();
      const me = sessionData.session?.user.id ?? "";
      const [rows, exercises] = await Promise.all([
        supabase.from("coach_equipment").select("key, hidden, name, icon_path").eq("coach_id", me),
        supabase.from("coach_exercises").select("equipment").eq("coach_id", me),
      ]);
      if (cancelled) return;
      // ⚠️ A FAILED READ IS NOT "NOTHING CHANGED" — it would draw every piece as shipped and invite a
      // coach to redo what he already did. One error state for the screen.
      const failed = rows.error ?? exercises.error;
      if (failed !== null && failed !== undefined) {
        setError(`Your equipment could not be read. ${failed.message}`);
        return;
      }
      setError(null);
      setSettings(rows.data ?? []);
      setUsage(usageCounts((exercises.data ?? []).map((r) => r.equipment)));
    })();
    return () => { cancelled = true; };
  }, []);

  // A click anywhere else closes the ⋯ menu; Escape too.
  useEffect(() => {
    if (menuFor === null) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      if (e instanceof PointerEvent && (e.target as Element).closest?.(".equipment-menu-anchor") !== null) return;
      setMenuFor(null);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", close); };
  }, [menuFor]);

  async function coachId(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    return data.session?.user.id ?? null;
  }

  /** Apply one write and fold it into the page — the same rule the editor saves by. */
  async function write(key: string, typed: string, iconPath: string | null, hidden: boolean): Promise<string | null> {
    const w = settingWrite(key, typed, iconPath, hidden);
    if (w.kind === "refuse") return w.reason;
    const id = await coachId();
    if (id === null) return "You are signed out. Sign in again.";
    const result = w.kind === "delete"
      ? await supabase.from("coach_equipment").delete().eq("key", key)
      : await supabase.from("coach_equipment").upsert({ coach_id: id, ...w.row }, { onConflict: "coach_id,key" });
    if (result.error !== null) return result.error.message;
    setSettings((s) => {
      const rest = (s ?? []).filter((r) => r.key !== key);
      return w.kind === "delete" ? rest : [...rest, w.row];
    });
    return null;
  }

  function current(key: string): EquipmentSetting | undefined {
    return (settings ?? []).find((s) => s.key === key);
  }

  async function setHidden(item: EquipmentItem, hide: boolean) {
    setBusyKey(item.key); setError(null);
    const row = current(item.key);
    const failure = await write(item.key, row?.name ?? "", row?.icon_path ?? null, hide);
    setBusyKey(null);
    if (failure !== null) setError(failure);
  }

  /** "Restore default": the row goes, and with it every change — name, icon, hidden. */
  async function restore(keys: readonly string[]) {
    if (keys.length === 0) return;
    setError(null);
    const { error: e } = await supabase.from("coach_equipment").delete().in("key", [...keys]);
    if (e !== null) { setError(e.message); return; }
    setSettings((s) => (s ?? []).filter((r) => !keys.includes(r.key)));
  }

  async function deleteOwn(key: string) {
    setError(null);
    const row = current(key);
    const { error: e } = await supabase.from("coach_equipment").delete().eq("key", key);
    if (e !== null) { setError(e.message); return; }
    // The uploaded file goes with it, best effort: a leftover icon costs storage, never correctness.
    const path = row?.icon_path;
    if (path !== null && path !== undefined && !path.startsWith(GLYPH_PREFIX)) {
      void supabase.storage.from(ICON_BUCKET).remove([path]);
    }
    setSettings((s) => (s ?? []).filter((r) => r.key !== key));
  }

  const all = settings ?? [];
  const sections = settings === null ? [] : equipmentPageSections(all, usage, query, filter);
  const total = allEquipmentKeys().length;
  const hiddenCount = all.filter((s) => s.hidden).length;
  const restorable = builtInsToRestore(all);
  const ownCount = all.filter((s) => isCustomKey(s.key)).length;

  return (
    <Workspace>
      <main id="main" className="workouts-page equipment-page library-page">
        {/* W72 step 5 — the Library's ONE masthead (the roster's `ours-head`): the title, the one primary at its right. */}
        <header className="ours-head">
          <div className="ours-head-text">
            <h1 className="ht-title ours-title" tabIndex={-1} ref={heading}>Library</h1>
          </div>
          {CUSTOM_EQUIPMENT_OPEN && settings !== null && (
            <button type="button" className="ht-button ht-button-primary equipment-new"
                    onClick={() => { remember(); setEditing({ mode: "new", key: null, name: "", iconPath: null }); }}>
              New equipment
            </button>
          )}
        </header>
        {/* W72 step 5 — the Library's four parts as ONE row under the title; the left sub-nav and its phone copy are gone. */}
        <LibraryTabs current="equipment" counts={libraryCounts} />

        {settings !== null && (
          <p className="ht-overline workouts-count">
            {`${String(total + ownCount)} PIECES`
              + (hiddenCount > 0 ? ` · ${String(hiddenCount)} HIDDEN` : "")
              + (restorable.length > 0 ? ` · ${String(restorable.length)} CHANGED` : "")}
          </p>
        )}


        <div className="equipment-bar">
          <label className="workouts-search">
            <span className="ht-visually-hidden">Search equipment</span>
            <span aria-hidden="true" className="workouts-search-glyph">
              <Icon name="search" size={16} />
            </span>
            <input className="ht-input workouts-search-input" type="search" value={query}
                   placeholder="Search equipment" onChange={(e) => setQuery(e.target.value)} />
          </label>
          {settings !== null && (
            <div className="equipment-filter" role="group" aria-label="Show">
              <button type="button" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>All</button>
              <button type="button" aria-pressed={filter === "changed"} onClick={() => setFilter("changed")}>
                {`Changed${restorable.length > 0 ? ` (${String(restorable.length)})` : ""}`}
              </button>
            </div>
          )}
          {restorable.length > 0 && (
            <button type="button" className="ht-button ht-button-quiet equipment-restore-all"
                    onClick={() => setConfirming({ kind: "restoreAll", keys: restorable })}>
              Restore all defaults
            </button>
          )}
        </div>

        {error !== null && <p className="ht-callout workouts-error" role="alert">{error}</p>}
        {settings === null && error === null && (
          <p className="ht-callout loading-line" aria-live="polite">Loading…</p>
        )}
        {settings !== null && (
          <p className="equipment-note">
            {filter === "changed"
              ? "Pieces you renamed, gave another icon, or hid. Restore one to put it back as the app ships it."
              : "A hidden piece is no longer offered when you build an exercise. Exercises that already use it keep it."}
          </p>
        )}

        {sections.map((section) => (
          <section key={section.name} className="equipment-group" aria-label={section.name}>
            <h2 className="equipment-group-name">{section.name}</h2>
            <ul className="equipment-list">
              {section.items.map((item) => (
                <li key={item.key} className={"equipment-row" + (item.hidden ? " is-hidden" : "")}>
                  <span className="equipment-row-icon">
                    <PieceIcon equipmentKey={item.key} iconPath={item.iconPath} size={24} />
                  </span>
                  <span className="equipment-row-text">
                    <span className="equipment-row-name">{item.label}</span>
                    <span className="equipment-row-use">
                      {item.usedBy === 0 ? "Not used yet"
                        : `Used by ${String(item.usedBy)} exercise${item.usedBy === 1 ? "" : "s"}`}
                      {item.hidden && " · Hidden"}
                      {item.changed && !item.custom && item.label !== item.originalLabel && ` · was ${item.originalLabel}`}
                    </span>
                  </span>
                  <span className="equipment-menu-anchor">
                    <button type="button" className="equipment-menu-button" aria-haspopup="menu"
                            aria-expanded={menuFor === item.key} aria-label={`Actions for ${item.label}`}
                            disabled={busyKey === item.key}
                            onClick={() => setMenuFor(menuFor === item.key ? null : item.key)}>
                      <Icon name="ellipsis" size={18} stroke={2.4} />
                    </button>
                    {menuFor === item.key && (
                      <ul className="equipment-menu" role="menu" aria-label={`Actions for ${item.label}`}>
                        <li role="none"><button type="button" role="menuitem" onClick={(e) => {
                          remember(rowMenuButton(e.currentTarget));
                          setMenuFor(null);
                          setEditing({ mode: "edit", key: item.key, name: current(item.key)?.name ?? "",
                                       iconPath: item.iconPath });
                        }}>Edit</button></li>
                        {CUSTOM_EQUIPMENT_OPEN && (
                          <li role="none"><button type="button" role="menuitem" onClick={(e) => {
                            remember(rowMenuButton(e.currentTarget));
                            setMenuFor(null);
                            const taken = new Set(all.filter((s) => isCustomKey(s.key))
                              .map((s) => equipmentName(s.key, all).toLowerCase()));
                            setEditing({ mode: "new", key: null, name: copyName(item.label, taken),
                                         iconPath: item.iconPath ?? (item.custom ? null : `${GLYPH_PREFIX}${item.key}`) });
                          }}>Copy</button></li>
                        )}
                        <li role="none"><button type="button" role="menuitem" onClick={() => {
                          setMenuFor(null); void setHidden(item, !item.hidden);
                        }}>{item.hidden ? "Show in my builder" : "Hide from my builder"}</button></li>
                        {item.changed && !item.custom && (
                          <li role="none"><button type="button" role="menuitem" onClick={() => {
                            setMenuFor(null); void restore([item.key]);
                          }}>Restore default</button></li>
                        )}
                        {item.custom && (
                          <li role="none"><button type="button" role="menuitem" className="is-danger" onClick={() => {
                            setMenuFor(null); setConfirming({ kind: "delete", key: item.key, label: item.label, usedBy: item.usedBy });
                          }}>Delete</button></li>
                        )}
                      </ul>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {settings !== null && sections.length === 0 && (
          <p className="ht-callout workouts-empty">
            {filter === "changed" && query.trim() === ""
              ? "Nothing changed — every piece is as the app ships it."
              : `Nothing matches “${query}”.`}
          </p>
        )}

        {editing !== null && (
          <EquipmentEditor target={editing} settings={all}
                           onClose={() => setEditing(null)}
                           onSave={async (key, name, iconPath) => {
                             const row = current(key);
                             return write(key, name, iconPath, row?.hidden ?? false);
                           }} />
        )}
        <EquipmentConfirmWindow confirm={confirming} onCancel={() => setConfirming(null)}
                       onConfirm={() => {
                         const c = confirming;
                         setConfirming(null);
                         if (c?.kind === "restoreAll") void restore(c.keys);
                         if (c?.kind === "delete") void deleteOwn(c.key);
                       }} />
      </main>
    </Workspace>
  );
}
